import { IBaseModel } from '../base/base-model.interface';
import { createEnumOptions } from '../shared/enum-option.interface';

export const TEST_PORT_ALIAS_PREFIX = 'Kolokacia_test';
export const PROD_PORT_ALIAS_PREFIX = 'Kolokacia';

export interface IColocation extends IBaseModel<'Colocation'> {
  isp?: string;

  servicePartner?: string;

  assignedUserId?: string;

  oltName: string;

  odbId?: string;

  sfp?: Sfp;

  linkSpeed?: LinkSpeed;

  note?: string;

  reservationDate?: Date;

  reservationSkipped?: boolean;

  state: ColocationState;

  fsp?: string;

  frame?: number;
  slot?: number;
  port?: number;

  price?: number;
  priceQuoteFile?: string;
  priceQuoteDate?: Date;
  priceQuotePublishedAt?: Date;

  ticketId?: string;
  // resolved from ticket service, not persisted on colocation
  ticketIntId?: string;
  priceQuoteApprovedAt?: Date;
  priceQuoteApprovedBy?: string;

  builtAt?: Date;
  finishedAt?: Date;

  constructionDate?: Date;
  technicianContact?: string;

  configReady?: boolean;

  odbPhotoBefore?: string[];
  popPhotoBefore?: string[];
  polePhotoBefore?: string[];
  netstorkPhotoBefore?: string[];

  odbPhotoAfter?: string[];
  popPhotoAfter?: string[];
  polePhotoAfter?: string[];
  netstorkPhotoAfter?: string[];
  attenuationMeasurementPhoto?: string[];

  setupFee?: number;

  internalId?: number;

  assignedMasterUserId?: string;
}

export const requiredFieldsForFinishConstruction: (keyof IColocation)[] = [
  'odbPhotoBefore',
  'popPhotoBefore',
  'polePhotoBefore',
  'netstorkPhotoBefore',
  'odbPhotoAfter',
  'popPhotoAfter',
  'polePhotoAfter',
  'netstorkPhotoAfter',
  'attenuationMeasurementPhoto',
];

// ODB/attenuation photos are only relevant for colocations with an ODB;
// if netstorkPhotoBefore was never captured, netstork photos aren't required either
export const getRequiredFieldsForFinishConstruction = (
  hasOdbId: boolean,
  hasNetstorkPhotoBefore: boolean,
): (keyof IColocation)[] =>
  requiredFieldsForFinishConstruction.filter((f) => {
    if (
      !hasOdbId &&
      (f === 'odbPhotoBefore' ||
        f === 'odbPhotoAfter' ||
        f === 'attenuationMeasurementPhoto')
    ) {
      return false;
    }
    if (
      !hasNetstorkPhotoBefore &&
      (f === 'netstorkPhotoBefore' || f === 'netstorkPhotoAfter')
    ) {
      return false;
    }
    return true;
  });

export const canFinishConstruction = (
  colocation: IColocation,
  userGroupMaster: boolean,
) => {
  return (
    userGroupMaster ||
    getRequiredFieldsForFinishConstruction(
      !!colocation.odbId,
      !!colocation.netstorkPhotoBefore?.length,
    ).every((f) => (colocation[f] as string[])?.length > 0)
  );
};

export enum ColocationState {
  awaitingApproval = 'awaitingApproval',
  reserved = 'reserved',
  priceQuote = 'priceQuote',
  awaitingConstruction = 'awaitingConstruction',
  built = 'built',
  finished = 'finished',
  cancelled = 'cancelled',
}

export const colocationClosedStates = [
  ColocationState.finished,
  ColocationState.cancelled,
];

export enum Sfp {
  km10 = 'km10',
  km20 = 'km20',
  km40 = 'km40',
  km60 = 'km60',
}

export enum LinkSpeed {
  gb1 = 'gb1',
  gb10 = 'gb10',
}

export const sfpOptions = createEnumOptions<Sfp>({
  [Sfp.km10]: { label: '10km' },
  [Sfp.km20]: { label: '20km' },
  [Sfp.km40]: { label: '40km' },
  [Sfp.km60]: { label: '60km' },
  _default: { label: '-' },
});

export const linkSpeedOptions = createEnumOptions<LinkSpeed>({
  [LinkSpeed.gb1]: { label: '1GB' },
  [LinkSpeed.gb10]: { label: '10GB' },
  _default: { label: '-' },
});

export const colocationStateOptions = createEnumOptions<ColocationState>({
  [ColocationState.awaitingApproval]: {
    label: 'Čaká na schválenie',
    color: 'primary',
  },
  [ColocationState.reserved]: { label: 'Rezervované', color: 'primary' },
  [ColocationState.priceQuote]: { label: 'Cenová ponuka', color: 'primary' },
  [ColocationState.awaitingConstruction]: {
    label: 'Čaká na výstavbu',
    color: 'primary',
  },
  [ColocationState.built]: { label: 'Vybudovaná', color: 'success' },
  [ColocationState.finished]: { label: 'Dokončená', color: 'success' },
  [ColocationState.cancelled]: { label: 'Zrušená', color: 'danger' },
  _default: { label: '-' },
});

export enum ColocationAction {
  reserveUplinkPort = 'reserveUplinkPort',
  cancelUplinkPortReservation = 'cancelUplinkPortReservation',
  continueWithoutReservation = 'continueWithoutReservation',
  orderConstruction = 'orderConstruction',
  assignToUser = 'assignToUser',
  assignToMaster = 'assignToMaster',
  offerPriceQuote = 'offerPriceQuote',
  publishPriceQuote = 'publishPriceQuote',
  cancelPriceQuote = 'cancelPriceQuote',
  rejectPriceQuote = 'rejectPriceQuote',
  finish = 'finish',
  reopen = 'reopen',
  cancel = 'cancel',
}

export type ColocationEmailSubTypes = ColocationAction | 'created';

export const colocationEmailsSubTypesOptions =
  createEnumOptions<ColocationEmailSubTypes>({
    created: { label: 'Vytvorenie kolokácie' },
    [ColocationAction.reserveUplinkPort]: { label: 'Rezervácia portu' },
    [ColocationAction.cancelUplinkPortReservation]: {
      label: 'Zrušenie rezervácie portu',
    },
    [ColocationAction.continueWithoutReservation]: {
      label: 'Pokračovanie bez rezervácie',
    },
    [ColocationAction.orderConstruction]: { label: 'Objednávka výstavby' },
    [ColocationAction.assignToUser]: { label: 'Priradenie užívateľovi' },
    [ColocationAction.assignToMaster]: {
      label: 'Priradenie užívateľovi TelcoHub',
    },
    [ColocationAction.offerPriceQuote]: { label: 'Cenová ponuka' },
    [ColocationAction.publishPriceQuote]: {
      label: 'Zverejnenie cenovej ponuky',
    },
    [ColocationAction.cancelPriceQuote]: { label: 'Zrušenie cenovej ponuky' },
    [ColocationAction.rejectPriceQuote]: {
      label: 'Zamietnutie cenovej ponuky',
    },
    [ColocationAction.finish]: { label: 'Dokončenie' },
    [ColocationAction.reopen]: { label: 'Znovuotvorenie' },
    [ColocationAction.cancel]: { label: 'Zrušenie' },
    _default: { label: '-' },
  });

export interface IColocationActionData {
  action: ColocationAction;
  frame?: string;
  slot?: string;
  port?: string;
  constructionDate?: Date;
  technicianContact?: string;
  spTechnicianContact?: string;
  priceQuoteFile?: string;
  price?: number;
  assignedUserId?: string | null;
  assignedMasterUserId?: string | null;
  setIspConstraint?: boolean;
}

export type ColocationInspection = 'general';

export const colocationInspectionLabels = new Map<ColocationInspection, string>(
  [['general', 'Návod']],
);

export type IColocationTutorialsOptions = Partial<
  Record<ColocationInspection, string>
>;

export interface IColocationOptions {
  tutorials?: IColocationTutorialsOptions;
}
